import { useToast } from '../../design-system/Toast';
import { useCallback, useEffect, useMemo, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type Announcements,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  type KeyboardCoordinateGetter,
} from '@dnd-kit/core';
import { bindingParams, parseService, runViewBinding, type BindingAction } from '@ayumia/component-runtime';
import { text } from '../../app/i18n';
import { Icon } from '../../design-system/icons';
import { cardZonesOf, type EventBindings, type KanbanColumn, type KanbanInputs } from '../assembly';
import { CommandDialog } from '../CommandDialog';
import type { Filters } from '../FilterBar';
import type { ObjectSummary } from '../format';
import { mergeFilters } from '../list-state';
import { useAppServices } from '../ViewContext';
import { CardZonesView, defaultZones, zoneFields, type CardZone } from './CardZones';
import { useCellRenderer, type CellRenderer, type Column } from './cells';
import { InfiniteSentinel } from './InfiniteSentinel';
import type { Selection } from './selection';
import { useCollection, type Row } from './useCollection';

/**
 * Renderer du composant Core `Kanban` (59 §7.1 ; ADR-096, ADR-098, ADR-099, ADR-100) : une colonne
 * par valeur de `column_field`, chacune la collection filtrée `column_field = valeur` sous la même
 * recherche et les mêmes filtres que la liste. Glisser-déposer **dnd-kit** — pointeur, tactile et
 * clavier, annonces aux lecteurs d'écran : curseur main, carte soulevée, place en pointillés,
 * colonne cible teintée ; une colonne inatteignable (aucune liaison `drop`, transition absente du
 * cycle de vie depuis l'état de la carte, commande non accordée) s'estompe. Le dépôt émet
 * `drop { target, expected_version, from, to, record }` et exécute la liaison de la colonne, sinon
 * celle de l'instance (une commande, réautorisée au serveur avec la version lue) ; la carte bouge
 * tout de suite et **revient** sur un refus, avec le motif. Aucune écriture directe, aucun moteur
 * de workflow ici.
 */

const PAGE = 20;
/** Un glisser commence après 6 px : un clic ouvre la carte, un geste la déplace. */
const DRAG_DISTANCE = 6;
/** Paramètres d'une liaison que la passerelle porte hors de la charge utile (cible, version lue, motif, idempotence). */
const ENVELOPE_PARAMS: ReadonlySet<string> = new Set(['target', 'expected_version', 'reason', 'idempotency_key']);
type Moved = { row: Row; to: string };
type DragData = { row: Row; from: string; title: string };

/** Liaison `drop` d'une colonne cible : la sienne, sinon celle de l'instance. */
function dropBinding(column: KanbanColumn, events: EventBindings | undefined): readonly BindingAction[] | undefined {
  return column.events?.['drop']?.actions ?? events?.['drop']?.actions;
}

/** Commande appelée en premier par une liaison (`api` vers `command:X`), s'il y en a une. */
function bindingCommand(actions: readonly BindingAction[] | undefined): string | undefined {
  const first = actions?.[0];
  if (first?.action !== 'api') return undefined;
  const service = parseService(first.service);
  return service?.kind === 'command' ? service.name : undefined;
}

interface Shared {
  type: string;
  object: ObjectSummary | undefined;
  kanban: KanbanInputs;
  /** Liaisons de l'instance (`drop` par défaut des colonnes sans liaison propre). */
  events?: EventBindings;
  listColumns: readonly Column[];
  baseFilter: Filters;
  search: { q?: string; q_fields?: readonly string[] };
  /** Tri de la liste, appliqué aux cartes de chaque colonne. */
  sort: { field: string; direction: 'asc' | 'desc' } | null;
  refreshKey: number;
  selection?: Selection;
  selectedId: string | null;
  onOpen: (row: Row) => void;
}

export function KanbanRenderer(props: Omit<Shared, 'refreshKey'> & { refreshKey: number; onChanged: () => void }) {
  const services = useAppServices();
  const { kanban, object } = props;
  const [localKey, setLocalKey] = useState(0);
  const toast = useToast();
  const [dialog, setDialog] = useState<{ row: Row; to: KanbanColumn; command: string; prefilled: Record<string, unknown> } | null>(null);
  const [active, setActive] = useState<DragData | null>(null);
  const [overLane, setOverLane] = useState<string | null>(null);
  const [moved, setMoved] = useState<ReadonlyMap<string, Moved>>(new Map());
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set(kanban.columns.filter((column) => column.collapsed === true).map((column) => column.value)));
  const refreshKey = props.refreshKey * 1000 + localKey;
  // Une relecture venue du conteneur (filtre, recherche…) remplace les déplacements optimistes.
  useEffect(() => setMoved(new Map()), [props.refreshKey]);

  const zones: readonly CardZone[] = cardZonesOf(kanban.card)?.zones ?? defaultZones(props.listColumns.slice(0, 3));
  const columnOf = (value: string): KanbanColumn | undefined => kanban.columns.find((column) => column.value === value);


  /**
   * Une colonne accepte une carte (présentation) : une liaison `drop`, une commande accordée
   * (`allowed`), et, quand les colonnes sont les états du cycle de vie, une transition déclarée
   * depuis l'état de la carte vers la colonne par cette commande. Le serveur tranche à l'exécution.
   */
  const canDrop = useCallback(
    (from: string, to: KanbanColumn): boolean => {
      if (from === to.value) return false;
      const actions = dropBinding(to, props.events);
      if (actions === undefined) return false;
      const command = bindingCommand(actions);
      if (command !== undefined && services.commands?.get(command)?.allowed === false) return false;
      const transitions = object?.transitions;
      if (transitions !== undefined && transitions.length > 0 && kanban.column_field === (object?.state_field ?? 'status')) {
        return transitions.some((transition) => transition.to === to.value && transition.from.includes(from) && (command === undefined || transition.command === command));
      }
      return true;
    },
    [props.events, services.commands, object, kanban.column_field],
  );

  const revert = (id: string): void =>
    setMoved((current) => {
      const next = new Map(current);
      next.delete(id);
      return next;
    });

  const after = (to: KanbanColumn): void => {
    toast('success', `Déplacé vers « ${text(to.label)} ».`);
    setLocalKey((value) => value + 1);
    props.onChanged();
  };

  const move = (row: Row, from: string, to: KanbanColumn): void => {
    if (typeof row.id !== 'string' || !canDrop(from, to)) {
      toast('danger', `Déplacement vers « ${text(to.label)} » non autorisé depuis cette colonne.`);
      return;
    }
    const id = row.id;
    const actions = dropBinding(to, props.events)!;
    // Charge de l'événement drop (manifeste du Kanban) : la liaison la lit sous $event.
    const event = { target: { type: props.type, id }, ...(typeof row.version === 'number' ? { expected_version: row.version } : {}), from, to: to.value, record: row };
    const options = { record: row, context: services.context, gateway: services.gateway };
    // Déplacement optimiste : la carte est tout de suite dans la colonne cible.
    setMoved((current) => new Map(current).set(id, { row, to: to.value }));
    const first = actions[0];
    const command = bindingCommand(actions);
    if (first?.action === 'api' && command !== undefined) {
      let params: Record<string, unknown>;
      try {
        params = bindingParams(first, event, options);
      } catch (error) {
        revert(id);
        toast('danger', `Déplacement impossible : ${error instanceof Error ? error.message : String(error)}`);
        return;
      }
      const prefilled = Object.fromEntries(Object.entries(params).filter(([name]) => !ENVELOPE_PARAMS.has(name)));
      // Une commande qui demande une saisie non fournie par la liaison passe par son formulaire déclaré.
      if (services.commands?.get(command)?.payload.some((field) => field.required === true && prefilled[field.field_id] === undefined)) {
        setDialog({ row, to, command, prefilled });
        return;
      }
    }
    void (async () => {
      const outcome = await runViewBinding(actions, event, options);
      if (outcome.status === 'completed') after(to);
      else {
        revert(id);
        toast('danger', `Déplacement refusé : ${outcome.error.message}`);
      }
    })();
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: DRAG_DISTANCE } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: laneCoordinates, keyboardCodes: { start: ['Space'], cancel: ['Escape'], end: ['Space', 'Enter'] } }),
  );
  // Le pointeur désigne la colonne survolée ; au clavier, l'intersection des rectangles.
  const collision: CollisionDetection = (args) => {
    const pointer = pointerWithin(args);
    return pointer.length > 0 ? pointer : rectIntersection(args);
  };
  const laneLabel = (id: string | number | undefined): string => {
    const column = typeof id === 'string' ? columnOf(id.replace(/^lane:/, '')) : undefined;
    return column === undefined ? '' : text(column.label);
  };
  const announcements: Announcements = {
    onDragStart: ({ active: dragged }) => `Carte ${(dragged.data.current as DragData | undefined)?.title ?? ''} saisie. Flèches gauche et droite pour changer de colonne, Espace pour déposer, Échap pour annuler.`,
    onDragOver: ({ over }) => (over === null ? 'Hors des colonnes.' : `Au-dessus de la colonne ${laneLabel(over.id)}.`),
    onDragEnd: ({ over }) => (over === null ? 'Carte reposée à sa place.' : `Carte déposée dans la colonne ${laneLabel(over.id)}.`),
    onDragCancel: () => 'Déplacement annulé, la carte reste à sa place.',
  };

  const onDragStart = (event: DragStartEvent): void => setActive((event.active.data.current as DragData | undefined) ?? null);
  const onDragOver = (event: DragOverEvent): void => setOverLane(typeof event.over?.id === 'string' ? event.over.id.replace(/^lane:/, '') : null);
  const onDragEnd = (event: DragEndEvent): void => {
    const data = event.active.data.current as DragData | undefined;
    setActive(null);
    setOverLane(null);
    const target = typeof event.over?.id === 'string' ? columnOf(event.over.id.replace(/^lane:/, '')) : undefined;
    if (data === undefined || target === undefined || target.value === data.from) return;
    move(data.row, data.from, target);
  };

  const shared: Shared = { ...props, refreshKey };
  const activeColumn = active === null ? undefined : columnOf(active.from);
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collision}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={() => {
        setActive(null);
        setOverLane(null);
      }}
      accessibility={{ announcements, screenReaderInstructions: { draggable: 'Pour déplacer une carte : Espace pour la saisir, flèches gauche et droite pour changer de colonne, Espace pour la déposer, Échap pour annuler. Entrée ouvre la carte.' } }}
    >
      <div className={`ay-kanban${active === null ? '' : ' ay-kanban--dragging'}`}>
        <div className="ay-kanban__lanes">
          {kanban.columns.map((column) => (
            <KanbanLane
              key={column.value}
              column={column}
              shared={shared}
              zones={zones}
              moved={moved}
              collapsed={collapsed.has(column.value)}
              onToggle={() =>
                setCollapsed((current) => {
                  const next = new Set(current);
                  if (next.has(column.value)) next.delete(column.value);
                  else next.add(column.value);
                  return next;
                })
              }
              dragFrom={active?.from ?? null}
              droppable={active === null ? true : canDrop(active.from, column)}
              over={overLane === column.value}
              canDrop={canDrop}
              onMove={move}
            />
          ))}
        </div>
      </div>
      <DragOverlay dropAnimation={{ duration: 180, easing: 'cubic-bezier(0.2, 0, 0, 1)' }}>
        {active !== null && activeColumn !== undefined ? <KanbanCardOverlay row={active.row} column={activeColumn} shared={shared} zones={zones} /> : null}
      </DragOverlay>
      {dialog !== null && services.commands?.get(dialog.command) !== undefined && (
        <CommandDialog
          command={services.commands.get(dialog.command)!}
          prefilled={dialog.prefilled}
          target={{ type: props.type, id: dialog.row.id as string }}
          {...(typeof dialog.row.version === 'number' ? { expectedVersion: dialog.row.version } : {})}
          onDone={() => {
            const to = dialog.to;
            setDialog(null);
            after(to);
          }}
          onClose={() => {
            // Saisie abandonnée : la carte revient à sa place.
            revert(dialog.row.id as string);
            setDialog(null);
          }}
        />
      )}
    </DndContext>
  );
}

/**
 * Clavier : les flèches gauche et droite sautent d'une colonne à l'autre (centre de la colonne
 * voisine), haut et bas restent dans la colonne. dnd-kit déplace la carte soulevée vers ces coordonnées.
 */
const laneCoordinates: KeyboardCoordinateGetter = (event, { context: { droppableRects, collisionRect } }) => {
  if (collisionRect === null) return undefined;
  const center = collisionRect.left + collisionRect.width / 2;
  if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
    event.preventDefault();
    return { x: collisionRect.left, y: collisionRect.top + (event.code === 'ArrowDown' ? 40 : -40) };
  }
  if (event.code !== 'ArrowRight' && event.code !== 'ArrowLeft') return undefined;
  event.preventDefault();
  const lanes = [...droppableRects.values()].sort((a, b) => a.left - b.left);
  const next = event.code === 'ArrowRight' ? lanes.find((rect) => rect.left + rect.width / 2 > center + 4) : [...lanes].reverse().find((rect) => rect.left + rect.width / 2 < center - 4);
  if (next === undefined) return undefined;
  return { x: next.left + next.width / 2 - collisionRect.width / 2, y: Math.max(next.top + 56, collisionRect.top) };
};

function KanbanLane({
  column,
  shared,
  zones,
  moved,
  collapsed,
  onToggle,
  dragFrom,
  droppable,
  over,
  canDrop,
  onMove,
}: {
  column: KanbanColumn;
  shared: Shared;
  zones: readonly CardZone[];
  moved: ReadonlyMap<string, Moved>;
  collapsed: boolean;
  onToggle: () => void;
  dragFrom: string | null;
  droppable: boolean;
  over: boolean;
  canDrop: (from: string, to: KanbanColumn) => boolean;
  onMove: (row: Row, from: string, to: KanbanColumn) => void;
}) {
  const { kanban, object } = shared;
  const filter = mergeFilters({ [kanban.column_field]: { eq: column.value } }, shared.baseFilter);
  const collection = useCollection({ type: shared.type, input: { filter, ...(shared.sort === null ? {} : { sort: [shared.sort] }), ...(shared.search.q === undefined ? {} : { q: shared.search.q, ...(shared.search.q_fields === undefined ? {} : { q_fields: shared.search.q_fields }) }) }, pageSize: PAGE, dropSortOnError: true }, shared.refreshKey);
  // Déplacements optimistes : une carte partie n'est plus ici, une carte arrivée y est déjà.
  const rows = useMemo(() => {
    const staying = collection.rows.filter((row) => {
      const entry = typeof row.id === 'string' ? moved.get(row.id) : undefined;
      return entry === undefined || entry.to === column.value;
    });
    const present = new Set(staying.map((row) => row.id));
    const arrived = [...moved.values()].filter((entry) => entry.to === column.value && !present.has(entry.row.id)).map((entry) => entry.row);
    return [...arrived, ...staying];
  }, [collection.rows, moved, column.value]);
  const render = useCellRenderer(object, shared.listColumns, zoneFields(zones), rows, collection.meta);
  const forbidden = dragFrom !== null && !droppable && dragFrom !== column.value;
  const { setNodeRef } = useDroppable({ id: `lane:${column.value}`, disabled: forbidden });
  const label = text(column.label);
  const tone = column.tone ?? 'neutral';
  const total = collection.total === undefined ? undefined : collection.total + rows.length - collection.rows.length;
  const count = total === undefined ? '…' : total.toLocaleString('fr');
  const state = dragFrom === null ? '' : dragFrom === column.value ? ' ay-kanban__lane--origin' : droppable ? ' ay-kanban__lane--droppable' : ' ay-kanban__lane--forbidden';
  const overLimit = column.limit !== undefined && total !== undefined && total > column.limit;
  const classes = `ay-kanban__lane ay-kanban__lane--tone-${tone}${state}${over && !forbidden ? ' ay-kanban__lane--over' : ''}`;

  if (collapsed) {
    return (
      <section ref={setNodeRef} className={`${classes} ay-kanban__lane--collapsed`} aria-label={label}>
        <button type="button" className="ay-kanban__expand" aria-expanded={false} onClick={onToggle} title={`Déplier « ${label} »`}>
          <span className="ay-kanban__dot" aria-hidden="true" />
          <span className="ay-kanban__vertical">{label}</span>
          <span className="ay-kanban__count">{count}</span>
        </button>
      </section>
    );
  }
  return (
    <section ref={setNodeRef} className={classes} aria-label={label}>
      <header className="ay-kanban__head">
        <span className="ay-kanban__dot" aria-hidden="true" />
        <strong className="ay-kanban__title">{label}</strong>
        <span className={`ay-kanban__count${overLimit ? ' ay-kanban__count--limit' : ''}`} title={column.limit === undefined ? undefined : `Limite : ${column.limit}`}>
          {count}
          {column.limit === undefined ? '' : ` / ${column.limit}`}
        </span>
        {kanban.collapsible !== false && (
          <button type="button" className="ay-kanban__collapse" aria-expanded={true} aria-label={`Replier « ${label} »`} onClick={onToggle}>
            <Icon name="chevron" size={14} />
          </button>
        )}
      </header>
      <div className="ay-kanban__cards">
        {forbidden && <p className="ay-kanban__forbidden">Déplacement non autorisé</p>}
        {collection.status === 'error' && collection.rows.length === 0 ? (
          <div role="alert" className="ay-banner ay-banner--danger">
            {collection.error?.message}{' '}
            <button type="button" className="ay-button ay-button--secondary" onClick={collection.reload}>
              Réessayer
            </button>
          </div>
        ) : collection.status === 'loading' && rows.length === 0 ? (
          <span className="ay-kanban__empty">Chargement…</span>
        ) : rows.length === 0 ? (
          <span className="ay-kanban__empty">Aucun élément</span>
        ) : (
          rows.map((row, index) => <KanbanCard key={typeof row.id === 'string' ? row.id : index} row={row} index={index} column={column} shared={shared} zones={zones} render={render} canDrop={canDrop} onMove={onMove} />)
        )}
        {collection.status === 'ready' && collection.rows.length > 0 && <InfiniteSentinel loadingMore={collection.loadingMore} done={collection.done} error={collection.moreError} count={collection.total ?? collection.rows.length} onMore={collection.loadMore} />}
      </div>
    </section>
  );
}

function KanbanCard({ row, index, column, shared, zones, render, canDrop, onMove }: { row: Row; index: number; column: KanbanColumn; shared: Shared; zones: readonly CardZone[]; render: CellRenderer; canDrop: (from: string, to: KanbanColumn) => boolean; onMove: (row: Row, from: string, to: KanbanColumn) => void }) {
  const id = typeof row.id === 'string' ? row.id : String(index);
  const first = zones[0];
  const title = first?.type === 'identity' ? render.display(first.title_field, row) : render.display(shared.listColumns[0]?.field ?? 'id', row);
  const targets = shared.kanban.columns.filter((candidate) => canDrop(column.value, candidate));
  const draggable = shared.kanban.drag !== false && targets.length > 0;
  const data: DragData = { row, from: column.value, title };
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `card:${id}`, data, disabled: !draggable });
  const checked = shared.selection?.has(id) ?? false;
  const open = (): void => shared.onOpen(row);
  const onKeyDown = (event: ReactKeyboardEvent<HTMLElement>): void => {
    if (event.key === 'Enter' && event.target === event.currentTarget) {
      event.preventDefault();
      open();
      return;
    }
    (listeners?.['onKeyDown'] as ((event: ReactKeyboardEvent<HTMLElement>) => void) | undefined)?.(event);
  };
  // Case et menu de la carte : ni glisser, ni ouverture.
  const stop = { onPointerDown: (event: { stopPropagation: () => void }) => event.stopPropagation(), onClick: (event: { stopPropagation: () => void }) => event.stopPropagation(), onKeyDown: (event: { stopPropagation: () => void }) => event.stopPropagation() };
  return (
    <article
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onKeyDown={onKeyDown}
      aria-roledescription={draggable ? 'carte déplaçable' : 'carte'}
      aria-label={title}
      data-opens-record
      onClick={open}
      className={`ay-kanban__card${draggable ? ' ay-kanban__card--draggable' : ''}${isDragging ? ' ay-kanban__card--placeholder' : ''}${shared.selectedId === id ? ' ay-kanban__card--selected' : ''}${checked ? ' ay-kanban__card--checked' : ''}`}
    >
      <CardFace row={row} shared={shared} zones={zones} render={render}>
        {shared.selection !== undefined && <input type="checkbox" className="ay-kanban__check" aria-label={`Sélectionner ${title}`} checked={checked} onChange={() => shared.selection!.toggle(id)} {...stop} />}
        {targets.length > 0 && (
          <label className="ay-kanban__move" {...stop}>
            <span className="ay-visually-hidden">Déplacer {title} vers</span>
            <select
              value=""
              onChange={(event) => {
                const to = targets.find((candidate) => candidate.value === event.target.value);
                if (to !== undefined) onMove(row, column.value, to);
              }}
            >
              <option value="">Déplacer vers…</option>
              {targets.map((candidate) => (
                <option key={candidate.value} value={candidate.value}>
                  {text(candidate.label)}
                </option>
              ))}
            </select>
          </label>
        )}
      </CardFace>
    </article>
  );
}

/** Contenu d'une carte : les zones JSON (ADR-100), la même définition que les cartes de liste. */
function CardFace({ row, shared, zones, render, children }: { row: Row; shared: Shared; zones: readonly CardZone[]; render: CellRenderer; children?: ReactNode }) {
  return (
    <>
      <CardZonesView zones={zones} row={row} object={shared.object} columns={shared.listColumns} render={render} />
      {children !== undefined && <div className="ay-kanban__cardtools">{children}</div>}
    </>
  );
}

/** Carte soulevée qui suit le pointeur pendant le glisser. */
function KanbanCardOverlay({ row, column, shared, zones }: { row: Row; column: KanbanColumn; shared: Shared; zones: readonly CardZone[] }) {
  const render = useCellRenderer(shared.object, shared.listColumns, zoneFields(zones), [row], undefined);
  return (
    <article className={`ay-kanban__card ay-kanban__card--lifted ay-kanban__lane--tone-${column.tone ?? 'neutral'}`}>
      <CardFace row={row} shared={shared} zones={zones} render={render} />
    </article>
  );
}
